import { Circle, FabricText, Group, Rect } from 'fabric';
import type { FabricObject } from 'fabric';
import { applyFill } from '../fill';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type { FillValue, ToolRecipe } from '../types';
import { CATEGORY_COLORS, CATEGORY_LABELS, ELEMENTS, elementBySymbol, elementByZ } from '../data/elements';
import type { ElementCategory, ElementInfo } from '../data/elements';

/**
 * Periodic-table element card (rebuilt SM-F2, 2026-07-23 — owner: "include
 * every element on the periodic table as an option … a ton of options with
 * these like different colors and fonts, and maybe even pictures of the
 * element?").
 *
 * The `element` param is a 118-entry picker (optgroup by period) + a final
 * "Custom" option. Pick a real element and EVERYTHING — symbol, name, atomic
 * number, mass, family, state, electron configuration, Bohr shells — comes from
 * the dataset (posterTools/data/elements), so a teacher never looks a value up.
 * "Custom" reveals the four manual text fields (the historic schema keys, kept
 * IDENTICAL so a card saved before this rebuild round-trips its data — a card
 * with no `element` param is treated as Custom and keeps its typed symbol/name/
 * number/mass; the default `element` is 'custom' for exactly that reason).
 *
 * "Pictures" decision (owner, already made): no photo assets exist, so the
 * optional richness is a DRAWN Bohr shell diagram — pure vector Circles, no
 * bitmaps — toggled by showShells. A licensed photo pack may come later.
 *
 * Three layouts (style): 'classic-tile' (the iconic square — number top-left,
 * big centred symbol, name/mass beneath), 'banner' (wide — a symbol tile beside
 * a facts column) and 'flashcard' (rounded, playful, the NAME as hero).
 *
 * Emits Rect / Circle / FabricText only, grouped and tagged {tool,version,
 * params} — plain fabric JSON, vector at any export dpi. version 2: the layout
 * is a deliberate redesign of the v1 miniCard tile, so the same params render
 * differently — bumped per the regenerate contract.
 *
 * BUILD B + fixes (owner 2026-07-23, "the fonts for each specific part"): the
 * number / name / mass / details text each take their own font, defaulting to
 * '' = inherit the Text font (so every saved card + grade band is byte-identical
 * — symbolFont is unchanged). Two live-test bugfixes ride along: (1) the default
 * element is now a real element ('He') so a NEW card opens filled, not in empty
 * Custom mode; (2) a Custom card derives its Bohr shells from the typed atomic
 * number (shells depend only on Z) so the diagram draws instead of silently
 * doing nothing. STILL version 2: for any params the old v2 could produce, the
 * output is identical — the only output that CHANGES is a Custom card that
 * asked for a Bohr diagram and previously got an empty one (a bug), so there is
 * nothing healthy to regenerate.
 */

// ---------------------------------------------------------------------------
// Defensive param coercion (the handwriting.ts / math.ts pattern) — params
// arrive untrusted (user-typed or replayed from stored JSON); generate() must
// never throw and never emit NaN geometry.
// ---------------------------------------------------------------------------

function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}

/** Unlike str(), an empty string is a MEANINGFUL value (a blank custom slot). */
function rawStr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

function bool(v: unknown, fallback: boolean): boolean {
  if (typeof v === 'boolean') return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return fallback;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
const TAU = Math.PI * 2;

const INK = '#0f172a';
const DEFAULT_FONT = 'Arial';
const DEFAULT_ACCENT = '#2563eb';
const CATEGORY_FALLBACK = '#cbd5e1';

/** Pull one representative solid color out of any FillValue (for a symbol tint
 *  or a thin stroke, which want a plain color even when a surface wears a
 *  gradient). */
function solidFromFill(fill: unknown, fallback: string): string {
  if (typeof fill === 'string') return fill.trim() || fallback;
  if (fill && typeof fill === 'object') {
    const f = fill as {
      type?: string;
      color?: string;
      from?: string;
      stops?: { color?: string }[];
    };
    if (f.type === 'solid' && typeof f.color === 'string') return f.color;
    if (f.type === 'linear-gradient') {
      if (Array.isArray(f.stops) && f.stops.length && f.stops[0].color) return f.stops[0].color;
      if (typeof f.from === 'string') return f.from;
    }
    if (f.type === 'radial-gradient' && Array.isArray(f.stops) && f.stops.length && f.stops[0].color) {
      return f.stops[0].color;
    }
  }
  return fallback;
}

/** Black or white ink, whichever reads on the given #rrggbb background. */
function readableInkOn(bg: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(bg).trim());
  if (!m) return INK;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const L = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return L > 0.6 ? INK : '#ffffff';
}

function stateLabel(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}

/** Shrink a font so `s` fits `maxW` at ~`em` average advance; never below 9. */
function fitFont(s: string, base: number, maxW: number, em = 0.58): number {
  const len = Math.max(1, s.length);
  const need = len * base * em;
  if (need <= maxW || maxW <= 0) return base;
  return Math.max(9, maxW / (len * em));
}

interface Txt {
  x: number;
  y: number;
  size: number;
  font: string;
  fill: string;
  originX?: 'left' | 'center' | 'right';
  originY?: 'top' | 'center' | 'bottom';
  weight?: string | number;
}

function text(content: string, o: Txt): FabricText {
  return new FabricText(content, {
    left: o.x,
    top: o.y,
    fontSize: o.size,
    fontFamily: o.font,
    fill: o.fill,
    originX: o.originX ?? 'left',
    originY: o.originY ?? 'top',
    fontWeight: o.weight ?? 'normal',
  });
}

// ---------------------------------------------------------------------------
// Resolved, coerced view of the params — computed once, handed to each layout.
// ---------------------------------------------------------------------------

interface FillJob {
  obj: FabricObject;
  fill: FillValue;
  fallback: string;
}

interface CardModel {
  symbol: string;
  name: string;
  number: string;
  mass: string;
  category: ElementCategory;
  config: string;
  state: string;
  shells: number[];
  isCustom: boolean;
  showCategory: boolean;
  showMass: boolean;
  showState: boolean;
  showConfig: boolean;
  showShells: boolean;
  autoColor: boolean;
  categoryColor: string;
  accentFill: FillValue;
  accentSolid: string;
  cardFill: FillValue;
  cardSolid: string;
  textColor: string;
  symbolFont: string;
  textFont: string;
  // Per-part fonts (BUILD B) — already resolved (part || textFont), so each
  // layout just reads the field for its part.
  numberFont: string;
  nameFont: string;
  massFont: string;
  detailFont: string;
}

/** Ink that reads on the accent surface (nucleus, category chip). */
function accentInk(m: CardModel): string {
  return readableInkOn(m.accentSolid);
}

/** Stamp a text as in-place editable (double-click → edit param → regenerate),
 *  but ONLY for custom cards — a dataset field is derived, not typed. */
function stampParam(t: FabricText, key: string, isCustom: boolean): FabricText {
  if (isCustom) t.set('data', { tableCell: { kind: 'param', key } });
  return t;
}

// ---------------------------------------------------------------------------
// Bohr shell diagram — concentric stroke-only rings + electron dots + an
// accent nucleus carrying the symbol. Pure Circles. Dots stay >= 3 units and
// space evenly even on a dense 32-electron period-7 shell.
// ---------------------------------------------------------------------------

function buildBohr(
  cx: number,
  cy: number,
  maxR: number,
  m: CardModel,
): FabricObject[] {
  const objs: FabricObject[] = [];
  const shells = m.shells.length ? m.shells : [1];
  const n = shells.length;
  const nucleusR = Math.max(18, maxR * 0.18);
  const span = Math.max(1, maxR - nucleusR);
  const ringGap = span / n;
  const dotR = clamp(ringGap * 0.24, 3, 7);
  const ink = m.accentSolid;

  for (let i = 0; i < n; i++) {
    const r = nucleusR + ringGap * (i + 1);
    objs.push(new Circle({
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      radius: r,
      fill: '',
      stroke: ink,
      strokeWidth: 2,
    }));
    const count = Math.max(0, Math.round(shells[i] || 0));
    if (count <= 0) continue;
    // Alternate rings get a half-step phase so dots never line up radially.
    const phase = -Math.PI / 2 + (i % 2) * (Math.PI / count);
    for (let k = 0; k < count; k++) {
      const a = phase + (k / count) * TAU;
      objs.push(new Circle({
        left: cx + r * Math.cos(a),
        top: cy + r * Math.sin(a),
        originX: 'center',
        originY: 'center',
        radius: dotR,
        fill: ink,
      }));
    }
  }
  objs.push(new Circle({
    left: cx,
    top: cy,
    originX: 'center',
    originY: 'center',
    radius: nucleusR,
    fill: ink,
  }));
  objs.push(text(m.symbol, {
    x: cx,
    y: cy,
    size: nucleusR * 0.92,
    font: m.symbolFont,
    fill: accentInk(m),
    originX: 'center',
    originY: 'center',
    weight: 'bold',
  }));
  return objs;
}

// ---------------------------------------------------------------------------
// Layout: CLASSIC TILE — number top-left, big centred symbol, name/mass under.
// ---------------------------------------------------------------------------

function buildClassicTile(m: CardModel): { objects: FabricObject[]; fills: FillJob[] } {
  const W = 360;
  const cx = W / 2;
  const padX = 24;
  const objects: FabricObject[] = [];
  const fills: FillJob[] = [];
  let y = 22;

  objects.push(stampParam(text(m.number, {
    x: padX, y, size: 30, font: m.numberFont, fill: m.textColor, weight: '600',
  }), 'number', m.isCustom));
  if (m.showState) {
    objects.push(text(stateLabel(m.state), {
      x: W - padX, y: y + 4, size: 20, font: m.detailFont, fill: m.textColor,
      originX: 'right', weight: '600',
    }));
  }
  y += 44;

  const symbolSize = 148;
  objects.push(stampParam(text(m.symbol, {
    x: cx, y, size: symbolSize, font: m.symbolFont, fill: m.accentSolid,
    originX: 'center', weight: 'bold',
  }), 'symbol', m.isCustom));
  y += symbolSize + 10;

  const nameSize = fitFont(m.name, 34, W - padX * 2);
  objects.push(stampParam(text(m.name, {
    x: cx, y, size: nameSize, font: m.nameFont, fill: m.textColor,
    originX: 'center', weight: '600',
  }), 'name', m.isCustom));
  y += nameSize * 1.3;

  if (m.showMass) {
    objects.push(stampParam(text(m.mass, {
      x: cx, y, size: 24, font: m.massFont, fill: m.textColor, originX: 'center',
    }), 'mass', m.isCustom));
    y += 24 * 1.4;
  }
  if (m.showConfig) {
    const cf = fitFont(m.config, 18, W - padX * 2, 0.55);
    objects.push(text(m.config, {
      x: cx, y, size: cf, font: m.detailFont, fill: m.textColor, originX: 'center',
    }));
    y += cf * 1.5;
  }
  if (m.showShells) {
    const bohrR = 132;
    objects.push(...buildBohr(cx, y + bohrR, bohrR, m));
    y += bohrR * 2 + 12;
  }
  if (m.showCategory) {
    const bandH = 44;
    const band = new Rect({ left: padX, top: y, width: W - padX * 2, height: bandH, rx: 10, ry: 10 });
    fills.push({ obj: band, fill: m.accentFill, fallback: m.accentSolid });
    objects.push(band);
    objects.push(text(CATEGORY_LABELS[m.category] || 'Element', {
      x: cx, y: y + bandH / 2, size: 22, font: m.detailFont, fill: accentInk(m),
      originX: 'center', originY: 'center', weight: '600',
    }));
    y += bandH + 4;
  }

  const H = y + 18;
  const bg = new Rect({
    left: 0, top: 0, width: W, height: H, rx: 22, ry: 22,
    stroke: m.accentSolid, strokeWidth: 6,
  });
  fills.push({ obj: bg, fill: m.cardFill, fallback: m.cardSolid });
  objects.unshift(bg);
  return { objects, fills };
}

// ---------------------------------------------------------------------------
// Layout: BANNER — a left symbol/Bohr tile beside a right facts column.
// ---------------------------------------------------------------------------

function buildBanner(m: CardModel): { objects: FabricObject[]; fills: FillJob[] } {
  const W = 600;
  const H = 320;
  const pad = 22;
  const panelW = 236;
  const objects: FabricObject[] = [];
  const fills: FillJob[] = [];

  const bg = new Rect({
    left: 0, top: 0, width: W, height: H, rx: 22, ry: 22,
    stroke: m.accentSolid, strokeWidth: 6,
  });
  fills.push({ obj: bg, fill: m.cardFill, fallback: m.cardSolid });
  objects.push(bg);

  // Left accent panel.
  const panel = new Rect({
    left: pad, top: pad, width: panelW, height: H - pad * 2, rx: 16, ry: 16,
  });
  fills.push({ obj: panel, fill: m.accentFill, fallback: m.accentSolid });
  objects.push(panel);

  const pcx = pad + panelW / 2;
  const ink = accentInk(m);
  objects.push(stampParam(text(m.number, {
    x: pad + 16, y: pad + 14, size: 28, font: m.numberFont, fill: ink, weight: '600',
  }), 'number', m.isCustom));

  if (m.showShells) {
    objects.push(...buildBohr(pcx, pad + (H - pad * 2) / 2, panelW / 2 - 24, {
      ...m,
      // Bohr rings/dots read as the panel's contrast ink here, not the accent.
      accentSolid: ink,
    }));
  } else {
    objects.push(stampParam(text(m.symbol, {
      x: pcx, y: pad + (H - pad * 2) / 2, size: 150, font: m.symbolFont, fill: ink,
      originX: 'center', originY: 'center', weight: 'bold',
    }), 'symbol', m.isCustom));
  }

  // Right facts column.
  const rx = pad + panelW + 28;
  const rMaxW = W - rx - pad;
  let y = pad + 18;
  const nameSize = fitFont(m.name, 46, rMaxW, 0.6);
  objects.push(stampParam(text(m.name, {
    x: rx, y, size: nameSize, font: m.nameFont, fill: m.textColor, weight: 'bold',
  }), 'name', m.isCustom));
  y += nameSize * 1.4;

  // Mass rides the Mass font; family / state / config ride the Details font
  // (both inherit textFont by default, so an untouched card is unchanged).
  const facts: { s: string; font: string }[] = [];
  if (m.showMass) facts.push({ s: `Atomic mass: ${m.mass}`, font: m.massFont });
  if (m.showCategory) facts.push({ s: `Family: ${CATEGORY_LABELS[m.category] || 'Element'}`, font: m.detailFont });
  if (m.showState) facts.push({ s: `State: ${stateLabel(m.state)}`, font: m.detailFont });
  if (m.showConfig) facts.push({ s: `Config: ${m.config}`, font: m.detailFont });
  for (const fact of facts) {
    const fs = fitFont(fact.s, 22, rMaxW, 0.55);
    objects.push(text(fact.s, {
      x: rx, y, size: fs, font: fact.font, fill: m.textColor,
    }));
    y += fs * 1.5;
  }
  return { objects, fills };
}

// ---------------------------------------------------------------------------
// Layout: FLASHCARD — rounded, playful; the NAME is the hero.
// ---------------------------------------------------------------------------

function buildFlashcard(m: CardModel): { objects: FabricObject[]; fills: FillJob[] } {
  const W = 420;
  const cx = W / 2;
  const pad = 26;
  const objects: FabricObject[] = [];
  const fills: FillJob[] = [];
  const badgeR = m.showShells ? 96 : 76;
  const badgeCy = pad + badgeR + 12;

  objects.push(stampParam(text(m.number, {
    x: pad, y: pad, size: 26, font: m.numberFont, fill: m.textColor, weight: '600',
  }), 'number', m.isCustom));
  if (m.showState) {
    objects.push(text(stateLabel(m.state), {
      x: W - pad, y: pad, size: 22, font: m.detailFont, fill: m.textColor, originX: 'right',
    }));
  }

  if (m.showShells) {
    objects.push(...buildBohr(cx, badgeCy, badgeR, m));
  } else {
    const badge = new Circle({
      left: cx, top: badgeCy, originX: 'center', originY: 'center', radius: badgeR,
    });
    fills.push({ obj: badge, fill: m.accentFill, fallback: m.accentSolid });
    objects.push(badge);
    objects.push(stampParam(text(m.symbol, {
      x: cx, y: badgeCy, size: badgeR * 0.85, font: m.symbolFont, fill: accentInk(m),
      originX: 'center', originY: 'center', weight: 'bold',
    }), 'symbol', m.isCustom));
  }

  let y = badgeCy + badgeR + 22;
  const nameSize = fitFont(m.name, 54, W - pad * 2, 0.55);
  objects.push(stampParam(text(m.name, {
    x: cx, y, size: nameSize, font: m.nameFont, fill: m.textColor,
    originX: 'center', weight: 'bold',
  }), 'name', m.isCustom));
  y += nameSize * 1.28;

  if (m.showMass) {
    objects.push(stampParam(text(m.mass, {
      x: cx, y, size: 26, font: m.massFont, fill: m.textColor, originX: 'center',
    }), 'mass', m.isCustom));
    y += 26 * 1.4;
  }
  if (m.showConfig) {
    const cf = fitFont(m.config, 18, W - pad * 2, 0.55);
    objects.push(text(m.config, {
      x: cx, y, size: cf, font: m.detailFont, fill: m.textColor, originX: 'center',
    }));
    y += cf * 1.5;
  }
  if (m.showCategory) {
    const bandH = 46;
    const band = new Rect({
      left: pad, top: y, width: W - pad * 2, height: bandH, rx: bandH / 2, ry: bandH / 2,
    });
    fills.push({ obj: band, fill: m.accentFill, fallback: m.accentSolid });
    objects.push(band);
    objects.push(text(CATEGORY_LABELS[m.category] || 'Element', {
      x: cx, y: y + bandH / 2, size: 22, font: m.detailFont, fill: accentInk(m),
      originX: 'center', originY: 'center', weight: '600',
    }));
    y += bandH + 6;
  }

  const H = y + pad;
  const bg = new Rect({
    left: 0, top: 0, width: W, height: H, rx: 32, ry: 32,
    stroke: m.accentSolid, strokeWidth: 8,
  });
  fills.push({ obj: bg, fill: m.cardFill, fallback: m.cardSolid });
  objects.unshift(bg);
  return { objects, fills };
}

// ---------------------------------------------------------------------------
// Option list — all 118 elements (optgroup by period) + a trailing Custom.
// ---------------------------------------------------------------------------

const ELEMENT_OPTIONS: { value: string; label: string; group?: string }[] = ELEMENTS
  .map((e) => ({
    value: e.symbol,
    label: `${e.z} · ${e.name} (${e.symbol})`,
    group: `Period ${e.period}`,
  }));
ELEMENT_OPTIONS.push({ value: 'custom', label: 'Custom (type your own)' });

const CUSTOM_WHEN = { key: 'element', equals: 'custom' } as const;

const elementCard: ToolRecipe = {
  id: 'element-card',
  domain: 'science',
  tags: ['periodic-table', 'chemistry', 'element', 'science-card', 'atoms', 'bohr-model', 'science'],
  featured: true,
  name: 'Element card',
  category: 'science',
  version: 2,
  description:
    'A periodic-table tile for any of the 118 elements — symbol, name, number, mass, family, state, electron configuration and an optional drawn Bohr model. Pick an element and every value is filled for you, or choose Custom to type your own.',
  defaults: {
    // Default to a REAL element ('He') so a NEW card opens filled — number,
    // symbol, name, mass, family band and (if toggled) the Bohr diagram all
    // populate from the dataset, instead of opening in Custom mode with blank
    // manual fields and an empty family/diagram. Back-compat is untouched: a
    // card saved before this rebuild has NO `element` param, and generate()'s
    // rawStr(params.element, 'custom') fallback still routes THAT case through
    // the Custom path (keying on the param being ABSENT, not on this default).
    element: 'He',
    symbol: 'He',
    name: 'Helium',
    number: '2',
    mass: '4.003',
    color: DEFAULT_ACCENT, // legacy accent — kept so old custom cards keep theirs
    showCategory: true,
    showMass: true,
    showState: false,
    showConfig: false,
    showShells: false,
    style: 'classic-tile',
    useCategoryColor: true,
    cardFill: '#ffffff',
    accentFill: DEFAULT_ACCENT,
    textColor: INK,
    symbolFont: DEFAULT_FONT,
    textFont: DEFAULT_FONT,
    // Per-part fonts (BUILD B) — '' = inherit textFont (resolved at generate).
    // Empty defaults keep every saved card + grade band byte-identical.
    numberFont: '',
    nameFont: '',
    massFont: '',
    detailFont: '',
  },
  schema: [
    {
      key: 'element',
      label: 'Element',
      type: 'select',
      options: ELEMENT_OPTIONS,
      section: 'Element',
      help: 'Pick any element — its number, symbol, name, mass and more fill in automatically.',
    },
    { key: 'symbol', label: 'Symbol', type: 'text', section: 'Element', visibleWhen: CUSTOM_WHEN },
    { key: 'name', label: 'Name', type: 'text', section: 'Element', visibleWhen: CUSTOM_WHEN },
    { key: 'number', label: 'Atomic number', type: 'text', section: 'Element', visibleWhen: CUSTOM_WHEN },
    { key: 'mass', label: 'Atomic mass', type: 'text', section: 'Element', visibleWhen: CUSTOM_WHEN },

    { key: 'showCategory', label: 'Show family band', type: 'boolean', section: 'What to show' },
    { key: 'showMass', label: 'Show atomic mass', type: 'boolean', section: 'What to show' },
    { key: 'showShells', label: 'Bohr diagram (electron shells)', type: 'boolean', section: 'What to show', help: 'A drawn shell diagram — rings of electrons around the nucleus.' },
    { key: 'showState', label: 'Show state (solid/liquid/gas)', type: 'boolean', section: 'What to show', advanced: true },
    { key: 'showConfig', label: 'Show electron configuration', type: 'boolean', section: 'What to show', advanced: true },

    {
      key: 'style',
      label: 'Card style',
      type: 'select',
      section: 'Style',
      options: [
        { value: 'classic-tile', label: 'Classic tile' },
        { value: 'banner', label: 'Banner (wide)' },
        { value: 'flashcard', label: 'Flashcard' },
      ],
    },
    {
      key: 'useCategoryColor',
      label: 'Auto-color by family',
      type: 'boolean',
      section: 'Style',
      help: 'Color the card accent from the element family (metals, noble gases…).',
    },
    { key: 'cardFill', label: 'Card background', type: 'fill', section: 'Style' },
    {
      key: 'accentFill',
      label: 'Accent color',
      type: 'fill',
      section: 'Style',
      visibleWhen: { key: 'useCategoryColor', equals: false },
      help: 'The symbol tint and band color when auto-color is off.',
    },
    { key: 'textColor', label: 'Text color', type: 'color', section: 'Style' },

    // Fonts (BUILD B) — the Text font is the default every part inherits;
    // symbolFont keeps its own family, and each of the four new parts falls
    // back to the Text font until the teacher sets it.
    { key: 'textFont', label: 'Text font (default for all parts)', type: 'font', section: 'Fonts' },
    { key: 'symbolFont', label: 'Symbol font', type: 'font', section: 'Fonts' },
    { key: 'numberFont', label: 'Number font', type: 'font', section: 'Fonts' },
    { key: 'nameFont', label: 'Name font', type: 'font', section: 'Fonts' },
    { key: 'massFont', label: 'Mass font', type: 'font', section: 'Fonts' },
    { key: 'detailFont', label: 'Details font', type: 'font', section: 'Fonts', help: 'Family band, state and electron configuration lines.' },
  ],
  // Hero the three decisions that shape the whole card; the sections read
  // Element / What to show / Style / Fonts. hideAdvanced tucks the two most
  // technical toggles (state, electron configuration) — the grade bands still
  // switch them on for the older students who need them.
  modal: {
    hero: ['element', 'style', 'useCategoryColor'],
    columns: 2,
    sectionOrder: ['Element', 'What to show', 'Style', 'Fonts'],
    hideAdvanced: true,
  },
  // Honest band values (schema members only): younger = playful flashcard with
  // a rounded face and the least text; older = the academic classic tile with
  // configuration + state. The Bohr model appears at 6-8 (atomic structure).
  gradeBands: {
    K2: { style: 'flashcard', showCategory: true, showMass: false, showState: false, showConfig: false, showShells: false, symbolFont: 'Fredoka', textFont: 'Fredoka' },
    G35: { style: 'flashcard', showCategory: true, showMass: true, showState: false, showConfig: false, showShells: false, symbolFont: 'Nunito', textFont: 'Nunito' },
    G68: { style: 'classic-tile', showCategory: true, showMass: true, showState: false, showConfig: false, showShells: true, symbolFont: 'Lexend', textFont: 'Lexend' },
    G912: { style: 'classic-tile', showCategory: true, showMass: true, showState: true, showConfig: true, showShells: false, symbolFont: 'Arial', textFont: 'Arial' },
  },
  async generate(params) {
    // element === 'custom' (or absent, or an unknown symbol) → the manual path;
    // otherwise pull EVERYTHING from the dataset.
    const elementParam = rawStr(params.element, 'custom');
    const info: ElementInfo | undefined =
      elementParam && elementParam !== 'custom' ? elementBySymbol(elementParam) : undefined;
    const isCustom = !info;

    const symbol = info ? info.symbol : rawStr(params.symbol, 'He');
    const name = info ? info.name : rawStr(params.name, 'Helium');
    const number = info ? String(info.z) : rawStr(params.number, '2');
    const mass = info ? info.mass : rawStr(params.mass, '4.003');
    const category: ElementCategory = info ? info.category : 'unknown';
    const config = info ? info.config : '';
    const state = info ? info.state : '';
    // Shell structure depends only on the atomic number (honest physics), so a
    // CUSTOM card can still draw a Bohr diagram: borrow the shells of whichever
    // real element shares the typed number (1–118). A number that isn't a real
    // Z leaves shells empty and the diagram skips cleanly (no crash, no rings).
    const shellSource: ElementInfo | undefined =
      info || (isCustom ? elementByZ(parseInt(number, 10)) : undefined);
    const shells = shellSource && Array.isArray(shellSource.shells)
      ? shellSource.shells.map((s) => Math.max(0, Math.round(Number(s) || 0)))
      : [];

    const showCategory = bool(params.showCategory, true);
    const showMass = bool(params.showMass, true);
    const showState = bool(params.showState, false) && state !== '';
    const showConfig = bool(params.showConfig, false) && config !== '';
    const showShells = bool(params.showShells, false) && shells.length > 0;

    const styleRaw = str(params.style, 'classic-tile');
    const style = styleRaw === 'banner' || styleRaw === 'flashcard' ? styleRaw : 'classic-tile';

    const useCategoryColor = bool(params.useCategoryColor, true);
    const categoryColor = CATEGORY_COLORS[category] || CATEGORY_FALLBACK;
    // Auto-color only means something for a REAL element with a known family;
    // a custom / unknown element falls through to the manual accent.
    const autoColor = useCategoryColor && !isCustom && category !== 'unknown';

    const accentFallback = str(params.color, DEFAULT_ACCENT); // old-card accent
    let accentFill: FillValue;
    let accentSolid: string;
    if (autoColor) {
      accentFill = categoryColor;
      accentSolid = categoryColor;
    } else {
      const rawAccent = params.accentFill;
      if (rawAccent == null || (typeof rawAccent === 'string' && rawAccent.trim() === '')) {
        accentFill = accentFallback;
        accentSolid = accentFallback;
      } else {
        accentFill = rawAccent as FillValue;
        accentSolid = solidFromFill(rawAccent, accentFallback);
      }
    }

    const cardFill: FillValue =
      params.cardFill == null || (typeof params.cardFill === 'string' && params.cardFill.trim() === '')
        ? '#ffffff'
        : (params.cardFill as FillValue);
    const cardSolid = solidFromFill(cardFill, '#ffffff');

    // Per-part fonts (BUILD B): the four new parts inherit the Text font when
    // unset ('' or absent) — so every card saved before this, and every grade
    // band (bands set only symbolFont/textFont), renders byte-identically.
    // symbolFont keeps its own pre-existing default and is NOT part of the
    // inheritance chain.
    const textFont = str(params.textFont, DEFAULT_FONT);
    const partFont = (key: string): string => {
      const v = params[key];
      return typeof v === 'string' && v.trim() !== '' ? v : textFont;
    };

    const model: CardModel = {
      symbol,
      name,
      number,
      mass,
      category,
      config,
      state,
      shells,
      isCustom,
      showCategory,
      showMass,
      showState,
      showConfig,
      showShells,
      autoColor,
      categoryColor,
      accentFill,
      accentSolid,
      cardFill,
      cardSolid,
      textColor: str(params.textColor, INK),
      symbolFont: str(params.symbolFont, DEFAULT_FONT),
      textFont,
      numberFont: partFont('numberFont'),
      nameFont: partFont('nameFont'),
      massFont: partFont('massFont'),
      detailFont: partFont('detailFont'),
    };

    const built =
      style === 'banner' ? buildBanner(model)
        : style === 'flashcard' ? buildFlashcard(model)
          : buildClassicTile(model);

    // Surfaces (card background, accent band/panel) resolve their FillValue
    // once here — a solid string resolves to itself (byte-identical to an inline
    // fill), a gradient/texture to the fabric object with a solid fallback.
    await Promise.all(built.fills.map((j) => applyFill(j.obj, j.fill, j.fallback)));

    const group = new Group(built.objects);
    group.set({ left: 0, top: 0 });
    return tagSmartObject(group, elementCard, params);
  },
};

registerRecipe(elementCard);
